import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  FileText,
  QrCode,
  Shield,
} from 'lucide-react';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import { PublicLayout } from '../components/layout/PublicLayout';
import { PartnerLogoCarousel } from '../components/partners/PartnerLogoCarousel';
import {
  CONFIRMED_PARTNERS,
  PARTNER_BENEFITS,
  PARTNER_MAIL_SUBJECTS,
  PARTNER_MATERIALS,
  PARTNER_TYPE_CARDS,
  PARTNER_USE_STEPS,
  partnerMailto,
} from '../content/partnersContent';
import '../styles/partners.css';

const shell = 'mx-auto w-full max-w-[75rem] px-4 sm:px-6 lg:px-8';

const primaryBtn =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-[hsl(152_28%_22%)] px-6 text-sm font-semibold text-[hsl(42_30%_96%)] transition hover:bg-[hsl(152_28%_18%)]';
const outlineBtn =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-[hsl(38_16%_78%)] bg-white px-6 text-sm font-medium text-[hsl(24_14%_16%)] transition hover:border-[hsl(38_36%_42%)]';

export default function PartnersPage() {
  const becomePartnerHref = partnerMailto(PARTNER_MAIL_SUBJECTS.becomePartner);
  const requestMaterialsHref = partnerMailto(
    PARTNER_MAIL_SUBJECTS.requestMaterials,
    'Please send partner materials for:\n\nOrganisation name:\nPartner type:\n',
  );
  const contactHref = partnerMailto(PARTNER_MAIL_SUBJECTS.contact);

  return (
    <PublicLayout className="partners-page">
      <header className="border-b border-[hsl(38_14%_86%)] bg-[hsl(42_28%_99%)]/95">
        <div className={`${shell} flex items-center justify-between gap-4 py-4`}>
          <div className="flex min-w-0 items-center gap-3">
            <Link
              to="/"
              className="inline-flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Home</span>
            </Link>
            <BrandWordmark variant="navLanding" className="hidden sm:inline" />
          </div>
          <Link to="/register" className={primaryBtn.replace('min-h-11', 'min-h-9 px-4')}>
            Start your archive
          </Link>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero */}
        <section className="partners-section partners-section--hero" aria-labelledby="partners-hero-heading">
          <div className={shell}>
            <p className="partners-eyebrow">Partners</p>
            <h1 id="partners-hero-heading" className="partners-heading mt-2 max-w-3xl">
              Partner with MyLegacyBridge
            </h1>
            <p className="partners-copy mt-3 max-w-2xl text-base sm:text-lg">
              Help families preserve stories, memories, memorials and legacy books with care.
            </p>

            <div className="mt-10 max-w-2xl rounded-2xl border border-[hsl(38_16%_82%)] bg-[hsl(42_28%_99%)] p-6 sm:p-8">
              <h2 className="font-serif text-xl font-light text-[hsl(24_14%_16%)] sm:text-2xl">
                Help families preserve what matters.
              </h2>
              <p className="partners-copy mt-3">
                MyLegacyBridge gives families a private place to preserve stories, voice memories, photos, documents,
                memorial pages and Legacy Books.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href={becomePartnerHref} className={primaryBtn}>
                  Become a partner
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </a>
                <a href={requestMaterialsHref} className={outlineBtn}>
                  Request partner materials
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Who we partner with */}
        <section className="partners-section" aria-labelledby="partners-types-heading">
          <div className={shell}>
            <p className="partners-eyebrow">Who we partner with</p>
            <h2 id="partners-types-heading" className="partners-heading mt-2 max-w-2xl">
              Organisations that support families through life&apos;s chapters
            </h2>
            <p className="partners-copy mt-3 max-w-2xl">
              Each card below shows an example partner type — not a confirmed organisation. We welcome conversations
              with groups who share our respect for privacy and remembrance.
            </p>
            <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {PARTNER_TYPE_CARDS.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.id} className="partners-type-card">
                    <p className="partners-type-card__label">Example partner type</p>
                    <span className="partners-type-card__icon">
                      <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden />
                    </span>
                    <h3 className="partners-type-card__title">{item.title}</h3>
                    <p className="partners-type-card__benefit">{item.benefit}</p>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* How partners use MyLegacyBridge */}
        <section className="partners-section partners-section--band" aria-labelledby="partners-how-heading">
          <div className={shell}>
            <p className="partners-eyebrow">How partners use MyLegacyBridge</p>
            <h2 id="partners-how-heading" className="partners-heading mt-2 max-w-2xl">
              Three simple steps
            </h2>
            <ol className="mt-10 grid gap-4 md:grid-cols-3">
              {PARTNER_USE_STEPS.map((item) => (
                <li key={item.step} className="partners-step">
                  <span className="partners-step__num">{item.step}</span>
                  <h3 className="mt-4 font-serif text-lg text-[hsl(24_14%_16%)]">{item.title}</h3>
                  <p className="partners-copy mt-2 text-sm">{item.copy}</p>
                </li>
              ))}
            </ol>
            <p className="partners-copy mt-8 max-w-3xl">
              Families can scan a QR code, start a private archive, create a memorial page, or preserve stories before
              they are lost.
            </p>
            <div className="partners-qr-note">
              <div className="flex items-start gap-3">
                <QrCode className="mt-0.5 h-5 w-5 shrink-0 text-[#c9a961]" strokeWidth={1.5} aria-hidden />
                <p>
                  Partners can display a MyLegacyBridge QR poster so families can scan, learn more and start preserving
                  memories when they are ready. Referral tracking and printable materials will connect to this system as
                  our partner programme grows.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Partner benefits */}
        <section className="partners-section partners-section--dark" aria-labelledby="partners-benefits-heading">
          <div className={shell}>
            <p className="partners-eyebrow">Partner benefits</p>
            <h2 id="partners-benefits-heading" className="partners-heading mt-2 max-w-2xl">
              A meaningful resource for the families you support
            </h2>
            <ul className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {PARTNER_BENEFITS.map((benefit) => (
                <li key={benefit} className="partners-benefit-card flex items-start gap-3">
                  <Shield className="mt-0.5 h-4 w-4 shrink-0 text-[#c9a961]" strokeWidth={1.75} aria-hidden />
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Printable materials */}
        <section className="partners-section" aria-labelledby="partners-materials-heading">
          <div className={shell}>
            <p className="partners-eyebrow">Partner materials</p>
            <h2 id="partners-materials-heading" className="partners-heading mt-2 max-w-2xl">
              Printable partner materials
            </h2>
            <p className="partners-copy mt-3 max-w-2xl">
              Printable materials are being prepared. Request what you need and we will share them when ready — no broken
              downloads.
            </p>
            <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {PARTNER_MATERIALS.map((material) => (
                <li key={material.id} className="partners-material-card">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[hsl(38_16%_82%)] bg-[hsl(42_28%_97%)]">
                      <FileText className="h-4 w-4 text-[hsl(38_36%_42%)]" strokeWidth={1.75} aria-hidden />
                    </span>
                    <div>
                      <h3 className="font-serif text-base text-[hsl(24_14%_16%)]">{material.label}</h3>
                      <p className="mt-0.5 text-xs text-[hsl(32_10%_42%)]">{material.description}</p>
                    </div>
                  </div>
                  <p className="partners-material-card__status">Printable materials being prepared</p>
                  <a
                    href={partnerMailto(
                      PARTNER_MAIL_SUBJECTS.requestMaterials,
                      `Material requested: ${material.label}\n\nOrganisation name:\nPartner type:\n`,
                    )}
                    className={outlineBtn.replace('min-h-11', 'min-h-10 w-full text-sm')}
                  >
                    Request materials
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Partner network */}
        <section className="partners-section partners-section--band" aria-labelledby="partners-network-heading">
          <div className={shell}>
            <p className="partners-eyebrow">Partner network</p>
            <h2 id="partners-network-heading" className="partners-heading mt-2 max-w-2xl">
              Trusted community partners
            </h2>
            <p className="partners-copy mt-3 max-w-2xl">
              When partnerships are confirmed, partner logos will appear here. We do not display placeholder or fictional
              organisation names.
            </p>
            <div className="mt-10">
              <PartnerLogoCarousel partners={CONFIRMED_PARTNERS} />
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="partners-final-cta" aria-labelledby="partners-final-heading">
          <div className={`${shell} max-w-2xl`}>
            <h2 id="partners-final-heading" className="partners-heading">
              Want to introduce MyLegacyBridge to families you support?
            </h2>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <a href={becomePartnerHref} className={primaryBtn}>
                Become a partner
                <ArrowRight className="h-4 w-4" aria-hidden />
              </a>
              <a href={contactHref} className={outlineBtn}>
                Contact us
              </a>
            </div>
          </div>
        </section>
      </main>
    </PublicLayout>
  );
}
